import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, cents } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import PickupPicker from "../components/PickupPicker";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";

export default function CartPage() {
  usePageMeta("Cart & Pickup | Tiff. Wellness Co.", "Review your juice stack, choose an available Dallas pickup window and check out securely with Stripe (test mode).", { robots: "noindex,nofollow" });
  const { items, count, bundle, split, nameMap, pickup, setPickup, setQty } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [favName, setFavName] = useState("");
  const [savingFav, setSavingFav] = useState(false);
  const navigate = useNavigate();

  const saveFavorite = async () => {
    setSavingFav(true);
    try {
      await api.post("/account/stacks", {
        name: favName.trim(),
        items: Object.entries(items).map(([slug, qty]) => ({ slug, qty })),
      });
      toast.success(`"${favName.trim()}" saved to your favourites`);
      setFavName("");
    } catch (e) { toast.error(errMsg(e)); }
    setSavingFav(false);
  };

  const checkout = async () => {
    if (!bundle || !pickup) return;
    setBusy(true);
    track("begin_checkout", { bundle_key: bundle.key, location_id: pickup.location_id });
    try {
      const { data } = await api.post("/checkout/bundle", {
        bundle_key: bundle.key,
        items: Object.entries(items).map(([slug, qty]) => ({ slug, qty })),
        location_id: pickup.location_id, window_id: pickup.window_id, pickup_date: pickup.date,
        origin_url: window.location.origin,
        email: user && user !== false ? undefined : email,
        phone: smsOptIn && phone ? phone : undefined,
        sms_opt_in: smsOptIn,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-4xl sm:text-5xl mb-8">Review &amp; Checkout</h1>
      {count === 0 ? (
        <div className="card-paper p-10 text-center">
          <p className="mb-4 text-sm text-[#171715]/70" data-testid="cart-page-empty">Your stack is empty.</p>
          <button className="btn-ink" data-testid="cart-page-build-cta" onClick={() => navigate("/build-your-stack")}>Build Your Stack</button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <div className="space-y-8">
            <section className="card-paper p-5">
              <h2 className="text-lg font-medium mb-4">Your bottles</h2>
              <ul className="space-y-3">
                {Object.entries(items).map(([slug, qty]) => (
                  <li key={slug} className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium">{nameMap[slug] || slug.replace(/-/g, " ")}</span>
                    <span className="flex items-center gap-1">
                      <button data-testid={`cartpage-dec-${slug}`} aria-label="decrease" className="min-h-[44px] min-w-[44px] border border-[#EBD9D0]" onClick={() => setQty(slug, qty - 1)}>−</button>
                      <span className="w-8 text-center font-mono">{qty}</span>
                      <button data-testid={`cartpage-inc-${slug}`} aria-label="increase" disabled={count >= 24} className="min-h-[44px] min-w-[44px] border border-[#EBD9D0] disabled:opacity-30" onClick={() => setQty(slug, qty + 1)}>+</button>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#171715]/55 mt-3">Order any number of bottles — pack pricing (3-pack &amp; 6-pack savings) applies automatically.</p>
              {user && user !== false && bundle && (
                <div className="mt-4 pt-4 border-t border-[#EBD9D0] flex flex-col sm:flex-row gap-2" data-testid="cartpage-save-fav-block">
                  <input data-testid="cartpage-fav-name" className="input-line flex-1" maxLength={40}
                    placeholder="Name this stack (e.g. Monday reset)" value={favName} onChange={(e) => setFavName(e.target.value)} />
                  <button data-testid="cartpage-save-fav-button" className="btn-ghost text-sm min-h-[44px] shrink-0 px-4"
                    disabled={!favName.trim() || savingFav} onClick={saveFavorite}>
                    {savingFav ? "Saving…" : "Save as favourite"}
                  </button>
                </div>
              )}
            </section>
            <section className="card-paper p-5">
              <h2 className="text-lg font-medium mb-4">Pickup</h2>
              <PickupPicker value={pickup} onChange={setPickup} />
            </section>
          </div>
          <aside className="lg:sticky lg:top-24 h-fit card-paper p-5">
            <h2 className="text-lg font-medium mb-3">Summary</h2>
            <p className="flex justify-between text-sm"><span>Bottles</span><span className="font-mono">{count}</span></p>
            {bundle && <p className="flex justify-between text-sm font-semibold mt-1"><span>{bundle.label}</span><span className="font-mono" data-testid="cartpage-total">{cents(bundle.cents)}</span></p>}
            {split && split.traditional > 0 && (
              <p data-testid="cartpage-split-note" className="text-xs text-[#171715]/65 mt-1">
                Functional ({split.functional}) {cents(split.functionalCents)} · Traditional ({split.traditional}) {cents(split.traditionalCents)}
              </p>
            )}
            <p className="text-xs text-[#171715]/55 mt-1">Tax (if applicable) is calculated at Stripe checkout.</p>
            {pickup ? (
              <p className="text-sm mt-3" data-testid="cartpage-pickup-summary"><span className="font-medium">{pickup.location_name}</span><br />{pickup.date} · {pickup.label}</p>
            ) : <p className="text-xs text-[#9c3b2a] mt-3" data-testid="cartpage-pickup-missing">Choose a pickup location and window to continue.</p>}
            {(!user || user === false) && (
              <div className="mt-4">
                <label className="text-xs font-medium block mb-1" htmlFor="guest-email">Email for order updates</label>
                <input id="guest-email" data-testid="cartpage-guest-email" type="email" className="input-line" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
            )}
            <div className="mt-4" data-testid="cartpage-sms-block">
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" data-testid="cartpage-sms-optin" className="mt-0.5" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)} />
                <span>Text me when my order is ready for pickup (optional). Reply STOP to opt out.</span>
              </label>
              {smsOptIn && (
                <input data-testid="cartpage-sms-phone" type="tel" className="input-line mt-2" placeholder="+1 214 555 0134" value={phone} onChange={(e) => setPhone(e.target.value)} />
              )}
            </div>
            <button data-testid="cartpage-checkout-button" className="btn-ink w-full mt-5" disabled={!bundle || !pickup || busy || ((!user || user === false) && !email) || (smsOptIn && !phone)} onClick={checkout}>
              {busy ? "Redirecting…" : "Checkout with Stripe (test)"}
            </button>
            <p className="text-xs text-[#171715]/55 mt-2">One-time purchase. Your slot is held for 30 minutes during checkout. Test mode — use card 4242 4242 4242 4242.</p>
          </aside>
        </div>
      )}
    </div>
  );
}
